/**
 * Local-storage helpers for Kickard.
 * No backend in Phase 1 — preferences and stats live here.
 */

const KEY = 'kickard.v1';

interface Stats {
  matchesPlayed: number;
  wins: number;
  losses: number;
  ties: number;       // resolved by penalty
  totalGoals: number;
  totalSaves: number;
}

interface Settings {
  soundEnabled: boolean;
  tutorialSeen: boolean;
  tutorialPrimerSeen: boolean;
  tutorialFirstBotMatchCompleted: boolean;
  tutorialSkipped: boolean;
  devEnabled: boolean;
  keepScreenAwake: boolean;
}

interface Store {
  stats: Stats;
  settings: Settings;
}

const DEFAULT_STORE: Store = {
  stats: {
    matchesPlayed: 0,
    wins: 0,
    losses: 0,
    ties: 0,
    totalGoals: 0,
    totalSaves: 0,
  },
  settings: {
    // Closed-alpha default: sound OFF until the player opts in. Toggle
    // is visible on Home AND in Settings.
    soundEnabled: false,
    tutorialSeen: false,
    tutorialPrimerSeen: false,
    tutorialFirstBotMatchCompleted: false,
    tutorialSkipped: false,
    devEnabled: false,
    keepScreenAwake: true,
  },
};

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return JSON.parse(JSON.stringify(DEFAULT_STORE));
    const parsed = JSON.parse(raw);
    return {
      stats: { ...DEFAULT_STORE.stats, ...(parsed.stats || {}) },
      settings: { ...DEFAULT_STORE.settings, ...(parsed.settings || {}) },
    };
  } catch {
    return JSON.parse(JSON.stringify(DEFAULT_STORE));
  }
}

function write(s: Store) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {}
}

export const storage = {
  getStats(): Stats { return read().stats; },
  getSettings(): Settings { return read().settings; },
  setSettings(patch: Partial<Settings>) {
    const cur = read();
    cur.settings = { ...cur.settings, ...patch };
    write(cur);
  },
  recordMatch(result: {
    winner: 'p1' | 'p2' | 'tie';
    p1Goals: number;
    p2Goals: number;
    p1Saves: number;
    p2Saves: number;
  }) {
    const cur = read();
    cur.stats.matchesPlayed += 1;
    if (result.winner === 'p1') cur.stats.wins += 1;
    else if (result.winner === 'p2') cur.stats.losses += 1;
    else cur.stats.ties += 1;
    cur.stats.totalGoals += result.p1Goals;
    cur.stats.totalSaves += result.p1Saves;
    write(cur);
  },
  reset() {
    write(JSON.parse(JSON.stringify(DEFAULT_STORE)));
  },
};
